/**
 * MakeHeader.jsx
 * -------------------------------------------------
 * Unified application header. Replaces the former near-duplicate pair
 * MainHeaderToki (general) and Make/MainHeader (make/timeline).
 *
 * The center widget is prop-driven:
 *   - showTimeline  -> ProgramTimelineNav (make / preview flows)
 *   - showJumpTo    -> "Jump to" burger menu (default flows)
 *   - neither       -> nothing
 *
 * Shared device / account logic lives in the useHeaderDevice /
 * useGoogleAccount hooks; repeated markup lives in the Header* parts.
 */
import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'

import {
    deviceDetails,
    deviceMismatch,
    showModal,
    webSerialConnect,
} from '../../../redux/actions'

import renderImage from '../../../source/importImg'
import style from './MakeHeader.module.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUpload } from '@fortawesome/free-solid-svg-icons'
import cacheAssets from '../../../utils/cacheAssets'
import secondaryImg from '../../../Assets/Bisoft_UI/Buttons/Inactive with shadow/save - secondary@2x.png'
import strokeImg from '../../../Assets/Bisoft_UI/Buttons/Inactive with shadow/help@2x.png'
import ProgramTimelineNav from '../../Make/ProgramTimelineNav'

import useHeaderDevice from './useHeaderDevice'
import useGoogleAccount from './useGoogleAccount'
import HeaderButton from './HeaderButton'
import HeaderDialogs from './HeaderDialogs'
import JumpToMenu from './JumpToMenu'
import {
    useHeaderHelp,
    HelpSlideModals,
    ConnectivityHelpOverlay,
} from './HeaderHelp'

import BatteryIndicator from '../ZingNotifications/BatteryIndicator'

function MakeHeader({
    webSerial,
    worker,
    webSerialConnect,
    title,
    showBluetoothBtn,
    showUsbBtn,
    useUsbIcon,
    connectionType,
    isExpansionKit: isExpansionKitProp,
    expansionKit: expansionKitProp,
    bluetoothConnectedOverride,
    usbConnectedOverride,
    connectOverride,
    showImport = false,
    handleImport,
    goBack,
    isGoBack,
    showUserAccount,
    showAccountSetting,
    deviceMismatch,
    device,
    deviceDetail,
    DeselectedComponent,
    concept,
    showFirmwareUpdateDialog,
    isShowSleep = false,
    socketData,
    showSave,
    isSaveToBoard = false,
    isShowNewUI = false,
    fileSaveClick = () => {},
    handelSleepIconClick = () => {},
    stopPA = () => {},
    showAssemblyHelp,
    showHexHelp,
    showConnectivityHelp,
    // center-widget selection
    showTimeline = false,
    showJumpTo = false,
    data,
    onJumpClick,
    // timeline props
    currentStep,
    totalSteps,
    stepTitles,
    onStepClick,
    completedStep = 0,
}) {
    const location = window.location
    const isKitRoute = location.pathname === '/kitMenu'

    const isAdminRoute =
        /admin/i.test(location.pathname) ||
        Boolean(title && /admin/i.test(title))

    const isExpansionKit = Boolean(
        expansionKitProp ||
        isExpansionKitProp ||
        sessionStorage.getItem('expansionKit') ||
        sessionStorage.getItem('connectedDevice') === 'woofKit' ||
        location.pathname === '/expansionKitSelection' ||
        Boolean(new URLSearchParams(location.search).get('expansionKitDevice'))
    )

    const isScreenRoute =
        /micropython/i.test(location.pathname) ||
        /playdynamex/i.test(location.pathname) ||
        /integrate/i.test(location.pathname) ||
        /simulate/i.test(location.pathname) ||
        /assembly/i.test(location.pathname) ||
        /playscreen/i.test(location.pathname) ||
        /make/i.test(location.pathname) ||
        /code/i.test(location.pathname)

    const isUsbMode =
        connectionType !== 'bluetooth' &&
        (useUsbIcon ||
            showUsbBtn ||
            connectionType === 'usb' ||
            isAdminRoute ||
            isExpansionKit ||
            isScreenRoute ||
            true)

    const shouldShowConnectionBtn = showBluetoothBtn || showUsbBtn
    const isDeviceConnected =
        webSerial.isConnected ||
        bluetoothConnectedOverride ||
        usbConnectedOverride

    const {
        history,
        connectedDevice,
        deviceVersion,
        deviceName,
        isDeviceMismatch,
        setIsDeviceMismatch,
        showErrorMsg,
        setshowErrorMsg,
        requestConnection,
        isConnecting,
        handleDevice,
        handleError,
    } = useHeaderDevice({
        webSerial,
        worker,
        socketData,
        device,
        concept,
        deviceMismatch,
        deviceDetail,
        DeselectedComponent,
        webSerialConnect,
        showFirmwareUpdateDialog,
    })

    const { googleUserData, imageError } = useGoogleAccount()

    const {
        isAssemblyHelp,
        toggleAssemblyHelp,
        isHexHelp,
        toggleHexHelp,
        isConnectivityHelp,
        toggleConnectivityHelp,
    } = useHeaderHelp()

    const [showPopup, setShowPopup] = useState(false)
    const [showDialogBoxes, setShowDialogBoxes] = useState({
        showErrorMsg: false,
        isDeviceMismatch: false,
        filePopup: false,
    })

    // Responsive title truncation limit
    const [charLimit, setCharLimit] = useState(24)
    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth
            if (width >= 1600) setCharLimit(45)
            else if (width >= 1368) setCharLimit(28)
            else if (width >= 1200) setCharLimit(24)
            else if (width >= 992) setCharLimit(20)
            else setCharLimit(16)
        }
        handleResize()
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    useEffect(() => {
        cacheAssets([
            renderImage('backBtn'),
            renderImage('clos'),
            renderImage('helpBtnInActive'),
            renderImage('UsbOn'),
            renderImage('UsbOff'),
            renderImage('buletooth'),
            renderImage('buletoothOff'),
        ])
    }, [])

    const handleClick = () => {
        history.push('/advanceSettingv1.0')
    }

    const handleSaveClick = () => {
        if (isShowNewUI || isSaveToBoard) {
            fileSaveClick()
            return
        }
        stopPA()
        setShowDialogBoxes({ ...showDialogBoxes, filePopup: true })
    }

    return (
        <>
            <HelpSlideModals
                isAssemblyHelp={isAssemblyHelp}
                toggleAssemblyHelp={toggleAssemblyHelp}
                isHexHelp={isHexHelp}
                toggleHexHelp={toggleHexHelp}
                zIndex={showTimeline ? '100005' : '10000'}
            />

            <HeaderDialogs
                isDeviceMismatch={isDeviceMismatch}
                setIsDeviceMismatch={setIsDeviceMismatch}
                handleDevice={handleDevice}
                connectedDevice={connectedDevice}
                deviceVersion={deviceVersion}
                deviceName={deviceName}
                webSerial={webSerial}
                device={device}
                history={history}
                showErrorMsg={showErrorMsg}
                setshowErrorMsg={setshowErrorMsg}
                handleError={handleError}
            />

            <div
                id={showTimeline ? 'make-main-header' : undefined}
                className={style.container}
                style={{
                    backgroundImage: !isKitRoute
                        ? `url(${renderImage('progressPanel')})`
                        : undefined,
                    ...(showTimeline ? { backgroundColor: 'white' } : {}),
                }}
            >
                {/* 1. LEFT SECTION */}
                <div className={style.leftSection}>
                    {(isGoBack || goBack) && (
                        <img
                            className={
                                webSerial.isConnected &&
                                (location.pathname == '/Selection' ||
                                    location.pathname == '/selection' ||
                                    location.pathname == '/biboxSelection')
                                    ? style.connected_back_btn
                                    : style.back_btn
                            }
                            src={renderImage('backBtn')}
                            onClick={async () => {
                                if (goBack) goBack()
                                else history.goBack()
                            }}
                            alt="Back"
                        />
                    )}
                </div>

                {/* 2. TITLE SECTION */}
                <div className={style.titleSection}>
                    <p
                        className={`${style.title} ${title && title.length > charLimit ? style.titleLong : ''}`}
                        title={title}
                    >
                        {title}
                    </p>
                </div>

                {/* 3. CENTER SECTION (timeline nav, make/preview flows) */}
                {showTimeline && (
                    <div className={style.centerSection}>
                        <ProgramTimelineNav
                            currentStep={currentStep}
                            totalSteps={totalSteps}
                            stepTitles={
                                stepTitles ||
                                data?.steps?.map(
                                    (step) =>
                                        step?.title || step?.stepTitle || ''
                                ) ||
                                []
                            }
                            onStepClick={onStepClick}
                            completedStep={completedStep}
                        />
                    </div>
                )}

                {/* 4. RIGHT ACTIONS SECTION */}
                <div className={style.rightActions}>
                    {/* Jump-to burger menu (default flows) */}
                    {!showTimeline && showJumpTo && (
                        <JumpToMenu data={data} onJumpClick={onJumpClick} />
                    )}

                    {/* Import Button */}
                    {showImport && (
                        <div className={style.importBtnWrapper}>
                            <label className={style.importBtn}>
                                <FontAwesomeIcon icon={faUpload} />
                                <input
                                    type="file"
                                    style={{ display: 'none' }}
                                    accept=".ag"
                                    onChange={handleImport}
                                />
                            </label>
                        </div>
                    )}

                    {/* Assembly Help Button */}
                    {showAssemblyHelp && (
                        <HeaderButton
                            variant="help"
                            src={strokeImg}
                            onClick={toggleAssemblyHelp}
                            alt="Help"
                            tooltip="Help"
                        />
                    )}

                    {/* Hex Help Button */}
                    {showHexHelp && (
                        <HeaderButton
                            variant="help"
                            src={strokeImg}
                            onClick={toggleHexHelp}
                            alt="Help"
                            tooltip="Help"
                        />
                    )}

                    {/* Device Connectivity Help Button */}
                    {showConnectivityHelp && (
                        <HeaderButton
                            variant="help"
                            src={renderImage('helpBtnInActive')}
                            onClick={toggleConnectivityHelp}
                            alt="Connectivity Help"
                            tooltip="Connection Help"
                        />
                    )}

                    {/* Fullscreen Connectivity Help Overlay */}
                    {showConnectivityHelp && isConnectivityHelp && (
                        <ConnectivityHelpOverlay
                            onClose={toggleConnectivityHelp}
                        />
                    )}

                    {/* Save Button */}
                    {showSave && (
                        <HeaderButton
                            variant="help"
                            src={secondaryImg}
                            onClick={handleSaveClick}
                            alt="Save"
                            tooltip="Save"
                        />
                    )}

                    {/* Settings Button */}
                    {showAccountSetting && (
                        <HeaderButton
                            variant="action"
                            src={renderImage('activeAdvanceSetting')}
                            onClick={handleClick}
                            alt="Settings"
                            tooltip="Setting"
                        />
                    )}

                    {/* Sleep Button */}
                    {isShowSleep && (
                        <HeaderButton
                            variant="action"
                            src={renderImage('zingSleepIcon')}
                            onClick={handelSleepIconClick}
                            alt="Sleep"
                            tooltip="Sleep"
                        />
                    )}

                    {/* User Profile */}
                    {showUserAccount &&
                        Object.keys(googleUserData).length > 0 && (
                            <div className={style.header_button}>
                                <img
                                    className={style.userAccount_Btn}
                                    style={{
                                        display: imageError ? 'none' : 'block',
                                    }}
                                    src={googleUserData.userAccount_Img}
                                    onClick={() => setShowPopup(!showPopup)}
                                    alt="User"
                                />
                                {showPopup && (
                                    <div
                                        className={style.popupCard}
                                        style={{ top: '60px', right: '0' }}
                                    >
                                        {/* ... popup content ... */}
                                    </div>
                                )}
                            </div>
                        )}

                    {/* Bluetooth / USB Button */}
                    {shouldShowConnectionBtn && (
                        <div className={style.web_button}>
                            <img
                                className={
                                    isUsbMode
                                        ? style.usb_Btn
                                        : style.bluetooth_Btn
                                }
                                src={
                                    isUsbMode
                                        ? isDeviceConnected
                                            ? renderImage('UsbOn')
                                            : renderImage('UsbOff')
                                        : isDeviceConnected
                                            ? renderImage('buletooth')
                                            : renderImage('buletoothOff')
                                }
                                onClick={
                                    isDeviceConnected || isConnecting
                                        ? undefined
                                        : connectOverride || requestConnection
                                }
                                style={
                                    isDeviceConnected
                                        ? { cursor: 'default', opacity: 0.85 }
                                        : isConnecting
                                            ? { cursor: 'wait', opacity: 0.6, pointerEvents: 'none' }
                                            : undefined
                                }
                                alt={isUsbMode ? 'USB' : 'Bluetooth'}
                            />
                            <span
                                className={`${style.tooltiptext} ${
                                    isUsbMode
                                        ? style.usbTooltip
                                        : style.bluetoothTooltip
                                }`}
                            >
                                <p>
                                    {isConnecting
                                        ? 'Connecting…'
                                        : isDeviceConnected
                                            ? isUsbMode
                                                ? 'USB connected'
                                                : 'Device connected'
                                            : isUsbMode
                                                ? 'USB connectivity'
                                                : 'Device connectivity'}
                                </p>
                            </span>
                            <BatteryIndicator />
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}

const mapStateToProps = (state) => {
    return state
}
const mapDispatchToProps = (dispatch) => {
    return {
        webSerialConnect: (data) => {
            dispatch(webSerialConnect(data))
        },
        showModal: (data) => {
            dispatch(showModal(data))
        },
        deviceMismatch: (data) => {
            dispatch(deviceMismatch(data))
        },
        deviceDetail: (data) => {
            dispatch(deviceDetails(data))
        },
        DeselectedComponent: (data) => {
            dispatch({ type: 'INTERNAL_ACCESSORIES', payload: data })
        },
        showFirmwareUpdateDialog: (payload) => {
            dispatch({ type: 'SHOW_FIRMWARE_UPDATE_DIALOG', payload })
        },
    }
}
export default connect(mapStateToProps, mapDispatchToProps)(MakeHeader)
